import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Copy, RefreshCw, RotateCcw, Trash2 } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import {
  useDeleteSurvivorGame,
  useRegenerateSurvivorInviteCode,
  useRestartSurvivorGame,
  useSurvivorGame,
  useSurvivorStandings,
  useUpdateSurvivorGame,
} from '../queries'
import { SurvivorGameStatus } from '../types'
import styles from './SobreviventeSettingsPage.module.css'

export function SobreviventeSettingsPage({ gameId }: { gameId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const gameQuery = useSurvivorGame(gameId)
  const standingsQuery = useSurvivorStandings(gameId)

  if (gameQuery.error instanceof ApiError) {
    return (
      <div className={styles.page}>
        <p className={styles.errorBox} role="alert">
          {errorMessage(gameQuery.error, t, 'error.survivor.load')}
        </p>
      </div>
    )
  }

  if (gameQuery.isPending || !gameQuery.data) {
    return <div className={styles.page}><div className={styles.skeleton} /></div>
  }

  const game = gameQuery.data

  if (!game.isOwner) {
    return (
      <div className={styles.page}>
        <p className={styles.errorBox} role="alert">
          Apenas o administrador do jogo pode acessar as configurações.
        </p>
        <button
          className={styles.btnGhost}
          onClick={() => void navigate({ to: '/sobrevivente/$gameId', params: { gameId }, search: { gw: undefined } })}
        >
          Voltar para o jogo
        </button>
      </div>
    )
  }

  const isLocked = game.status !== SurvivorGameStatus.RegistrationOpen
  const otherParticipants = (standingsQuery.data?.alive ?? []).filter((p) => p.userId !== game.ownerId)

  return (
    <div className={styles.page}>
      {isLocked && (
        <div className={styles.lockedBanner} role="status">
          O jogo já começou — alterações estão bloqueadas.
        </div>
      )}

      <RenameSection gameId={gameId} currentName={game.name} disabled={isLocked} />
      <TransferOwnerSection
        gameId={gameId}
        currentOwnerName={game.ownerDisplayName}
        candidates={otherParticipants}
        disabled={isLocked}
      />
      <InviteCodeSection gameId={gameId} currentCode={game.joinCode} />
      {/* Only after the game is over, and only while the competition still has rounds to play
          it over — the server decides both, and hands the answer down as canRestart. */}
      {game.canRestart && (
        <RestartSection
          gameId={gameId}
          seasonNumber={game.seasonNumber}
          onRestarted={(newGameId) => void navigate({
            to: '/sobrevivente/$gameId',
            params: { gameId: newGameId },
            search: { gw: undefined },
          })}
        />
      )}
      <DeleteSection
        gameId={gameId}
        gameName={game.name}
        disabled={isLocked}
        onDeleted={() => void navigate({ to: '/sobrevivente' })}
      />
    </div>
  )
}

// ── Rename ─────────────────────────────────────────────────────────────────

function RenameSection({
  gameId, currentName, disabled,
}: {
  gameId: string
  currentName: string
  disabled: boolean
}) {
  const [name, setName] = useState(currentName)
  const update = useUpdateSurvivorGame(gameId)
  const hasChange = name.trim() !== currentName && name.trim().length >= 3

  return (
    <Section title="Nome do jogo">
      <input
        className={styles.input}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={40}
        disabled={disabled || update.isPending}
      />
      <button
        type="button"
        className={styles.btnPrimary}
        disabled={!hasChange || disabled || update.isPending}
        onClick={() => update.mutate({ name: name.trim() })}
      >
        {update.isPending ? 'Salvando…' : 'Salvar'}
      </button>
    </Section>
  )
}

// ── Transfer ownership ───────────────────────────────────────────────────────

function TransferOwnerSection({
  gameId, currentOwnerName, candidates, disabled,
}: {
  gameId: string
  currentOwnerName: string
  candidates: { userId: string; displayName: string }[]
  disabled: boolean
}) {
  const [target, setTarget] = useState('')
  const update = useUpdateSurvivorGame(gameId)

  return (
    <Section title="Administrador">
      <p className={styles.helper}>
        Atual: <strong>{currentOwnerName}</strong>
      </p>
      {candidates.length === 0 ? (
        <p className={styles.helper}>Nenhum outro participante para transferir.</p>
      ) : (
        <>
          <select
            className={styles.select}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={disabled || update.isPending}
          >
            <option value="">Escolha um participante…</option>
            {candidates.map((c) => (
              <option key={c.userId} value={c.userId}>{c.displayName}</option>
            ))}
          </select>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={!target || disabled || update.isPending}
            onClick={() => update.mutate({ ownerId: target }, {
              onSuccess: () => setTarget(''),
            })}
          >
            {update.isPending ? 'Transferindo…' : 'Transferir'}
          </button>
        </>
      )}
    </Section>
  )
}

// ── Invite code ───────────────────────────────────────────────────────────────

function InviteCodeSection({
  gameId, currentCode,
}: {
  gameId: string
  currentCode: string
}) {
  const regenerate = useRegenerateSurvivorInviteCode(gameId)
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(currentCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard not available */ }
  }

  return (
    <Section title="Código de convite">
      <div className={styles.codeRow}>
        <span className={styles.codeBig}>{currentCode}</span>
        <button
          type="button"
          className={styles.btnGhost}
          onClick={handleCopy}
        >
          <Copy size={14} aria-hidden="true" /> {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <button
        type="button"
        className={styles.btnSecondary}
        onClick={() => regenerate.mutate()}
        disabled={regenerate.isPending}
      >
        <RefreshCw size={14} aria-hidden="true" />
        {regenerate.isPending ? 'Gerando…' : 'Gerar novo código'}
      </button>
    </Section>
  )
}

// ── Restart (new season) ─────────────────────────────────────────────────────

function RestartSection({
  gameId, seasonNumber, onRestarted,
}: {
  gameId: string
  seasonNumber: number
  onRestarted: (newGameId: string) => void
}) {
  const { t } = useTranslation()
  const restart = useRestartSurvivorGame(gameId)

  return (
    <Section title="Nova temporada">
      <p className={styles.helper}>
        Recomeça o jogo com os mesmos participantes na temporada {seasonNumber + 1}. O código de
        convite continua o mesmo e novas pessoas podem entrar até o fechamento da primeira rodada.
        Esta temporada fica guardada no hall da fama.
      </p>
      <button
        type="button"
        className={styles.btnPrimary}
        disabled={restart.isPending}
        onClick={() => restart.mutate(undefined, {
          onSuccess: (result) => onRestarted(result.gameId),
        })}
      >
        <RotateCcw size={14} aria-hidden="true" />
        {restart.isPending ? 'Iniciando…' : 'Iniciar nova temporada'}
      </button>
      {restart.error instanceof ApiError && (
        <p className={styles.errorBox} role="alert">
          {errorMessage(restart.error, t, 'error.survivor.restart')}
        </p>
      )}
    </Section>
  )
}

// ── Delete ────────────────────────────────────────────────────────────────────

function DeleteSection({
  gameId, gameName, disabled, onDeleted,
}: {
  gameId: string
  gameName: string
  disabled: boolean
  onDeleted: () => void
}) {
  const [confirm, setConfirm] = useState('')
  const del = useDeleteSurvivorGame()
  const canDelete = confirm.trim() === gameName.trim()

  return (
    <Section title="Excluir jogo" tone="danger">
      <p className={styles.helper}>
        Para confirmar, digite o nome do jogo: <strong>{gameName}</strong>
      </p>
      <input
        className={styles.input}
        type="text"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        disabled={disabled || del.isPending}
        placeholder={gameName}
      />
      <button
        type="button"
        className={styles.btnDanger}
        disabled={!canDelete || disabled || del.isPending}
        onClick={() => del.mutate(gameId, { onSuccess: onDeleted })}
      >
        <Trash2 size={14} aria-hidden="true" />
        {del.isPending ? 'Excluindo…' : 'Excluir jogo'}
      </button>
    </Section>
  )
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function Section({
  title, tone, children,
}: {
  title: string
  tone?: 'danger'
  children: React.ReactNode
}) {
  return (
    <section className={tone === 'danger' ? styles.cardDanger : styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.cardBody}>{children}</div>
    </section>
  )
}
